import { supabase } from '../lib/supabase';
import { emailService } from './emailService';

const toCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    projectNumber: obj?.project_number,
    quoteId: obj?.quote_id,
    orderId: obj?.order_id,
    clientId: obj?.client_id,
    assignedTo: obj?.assigned_to,
    title: obj?.title,
    description: obj?.description,
    status: obj?.status,
    startDate: obj?.start_date,
    endDate: obj?.end_date,
    estimatedCompletion: obj?.estimated_completion,
    actualCompletion: obj?.actual_completion,
    progressPercentage: obj?.progress_percentage,
    budget: obj?.budget,
    actualCost: obj?.actual_cost,
    tags: obj?.tags || [],
    attachments: obj?.attachments || [],
    notes: obj?.notes,
    metadata: obj?.metadata,
    createdBy: obj?.created_by,
    createdAt: obj?.created_at,
    updatedAt: obj?.updated_at,
  };
};

const toSnakeCase = (obj) => {
  return {
    project_number: obj?.projectNumber,
    quote_id: obj?.quoteId,
    order_id: obj?.orderId,
    client_id: obj?.clientId,
    assigned_to: obj?.assignedTo,
    title: obj?.title,
    description: obj?.description,
    status: obj?.status,
    start_date: obj?.startDate,
    end_date: obj?.endDate,
    estimated_completion: obj?.estimatedCompletion,
    actual_completion: obj?.actualCompletion,
    progress_percentage: obj?.progressPercentage,
    budget: obj?.budget,
    actual_cost: obj?.actualCost,
    tags: obj?.tags,
    attachments: obj?.attachments,
    notes: obj?.notes,
    metadata: obj?.metadata,
  };
};

const milestoneToCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    projectId: obj?.project_id,
    title: obj?.title,
    description: obj?.description,
    status: obj?.status,
    dueDate: obj?.due_date,
    completedAt: obj?.completed_at,
    deliverables: obj?.deliverables || [],
    sortOrder: obj?.sort_order,
    createdBy: obj?.created_by,
    createdAt: obj?.created_at,
    updatedAt: obj?.updated_at,
  };
};

const milestoneToSnakeCase = (obj) => {
  return {
    project_id: obj?.projectId,
    title: obj?.title,
    description: obj?.description,
    status: obj?.status,
    due_date: obj?.dueDate,
    completed_at: obj?.completedAt,
    deliverables: obj?.deliverables,
    sort_order: obj?.sortOrder,
  };
};

const create = async (projectData) => {
  const { data, error } = await supabase?.from('projects')?.insert([{
      ...toSnakeCase(projectData),
      project_number: `PRJ-${Date.now()}`,
      progress_percentage: 0
    }])?.select()?.single();

  if (error) throw error;
  return toCamelCase(data);
};

const update = async (id, projectData) => {
  const { data, error } = await supabase?.from('projects')?.update(toSnakeCase(projectData))?.eq('id', id)?.select()?.single();

  if (error) throw error;
  return toCamelCase(data);
};

const deleteMethod = async (id) => {
  const { error } = await supabase?.from('projects')?.delete()?.eq('id', id);

  if (error) throw error;
};

const getActivities = async (projectId) => {
  const { data, error } = await supabase?.from('activities')?.select('*')?.eq('project_id', projectId)?.order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

const createActivity = async (activityData) => {
  const snakeData = {
    project_id: activityData?.projectId,
    activity_type: activityData?.activityType || activityData?.type,
    title: activityData?.title,
    description: activityData?.description,
    user_id: activityData?.userId,
  };
  const { data, error } = await supabase?.from('activities')?.insert([snakeData])?.select()?.single();

  if (error) throw error;
  return data;
};

export const projectService = {
  async getProjects(filters = {}) {
    let query = supabase
      ?.from('projects')
      ?.select('*, leads(name, email, phone), user_profiles!projects_assigned_to_fkey(full_name)')
      ?.order('created_at', { ascending: false });

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    if (filters?.clientId) {
      query = query?.eq('client_id', filters?.clientId);
    }

    if (filters?.assignedTo) {
      query = query?.eq('assigned_to', filters?.assignedTo);
    }

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async getAll() {
    const { data, error } = await supabase
      ?.from('projects')
      ?.select('*, leads(name, email, phone), user_profiles!projects_assigned_to_fkey(full_name)')
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async getById(id) {
    const { data, error } = await supabase
      ?.from('projects')
      ?.select('*, leads(name, email, phone), user_profiles!projects_assigned_to_fkey(full_name)')
      ?.eq('id', id)
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async create(project) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Generate project number
    const { data: projectNumber } = await supabase?.rpc('generate_project_number');

    const { data, error } = await supabase
      ?.from('projects')
      ?.insert({
        ...toSnakeCase(project),
        project_number: projectNumber,
        created_by: user?.id,
      })
      ?.select()
      ?.single();

    if (error) throw error;
    
    const createdProject = toCamelCase(data);
    
    // Send project created email
    try {
      const clientData = await supabase?.from('leads')?.select('name, email')?.eq('id', project?.clientId)?.single();
      
      if (clientData?.data?.email) {
        await emailService?.sendProjectCreatedEmail(
          createdProject,
          clientData?.data?.email,
          clientData?.data?.name
        );
      }
    } catch (emailError) {
      console.error('Failed to send project created email:', emailError);
    }
    
    return createdProject;
  },

  async update(id, project) {
    const { data, error } = await supabase
      ?.from('projects')
      ?.update(toSnakeCase(project))
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;
    
    const updatedProject = toCamelCase(data);
    
    // Send project update email
    try {
      const projectDetails = await supabase
        ?.from('projects')
        ?.select('*, leads(name, email)')
        ?.eq('id', id)
        ?.single();
      
      const clientEmail = projectDetails?.data?.leads?.email;
      const clientName = projectDetails?.data?.leads?.name;
      
      if (clientEmail) {
        await emailService?.sendProjectUpdateEmail(
          updatedProject,
          clientEmail,
          clientName,
          'Votre projet a été mis à jour'
        );
      }
    } catch (emailError) {
      console.error('Failed to send project update email:', emailError);
    }
    
    return updatedProject;
  },

  async updateStatus(id, status) {
    const updateData = { status };
    if (status === 'completed') {
      updateData.actual_completion = new Date()?.toISOString();
      updateData.progress_percentage = 100;
    }

    const { data, error } = await supabase
      ?.from('projects')
      ?.update(updateData)
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;
    
    const updatedProject = toCamelCase(data);
    
    // Send project completed email if status is completed
    if (status === 'completed') {
      try {
        const projectDetails = await supabase
          ?.from('projects')
          ?.select('*, leads(name, email)')
          ?.eq('id', id)
          ?.single();
        
        const clientEmail = projectDetails?.data?.leads?.email;
        const clientName = projectDetails?.data?.leads?.name;
        
        if (clientEmail) {
          await emailService?.sendProjectCompletedEmail(
            updatedProject,
            clientEmail,
            clientName
          );
        }
      } catch (emailError) {
        console.error('Failed to send project completed email:', emailError);
      }
    }
    
    return updatedProject;
  },

  async delete(id) {
    const { error } = await supabase?.from('projects')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  // Milestone methods
  async getMilestones(projectId) {
    const { data, error } = await supabase
      ?.from('project_milestones')
      ?.select('*')
      ?.eq('project_id', projectId)
      ?.order('sort_order', { ascending: true });

    if (error) throw error;
    return data?.map(milestoneToCamelCase) || [];
  },

  async createMilestone(milestone) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('project_milestones')
      ?.insert({
        ...milestoneToSnakeCase(milestone),
        created_by: user?.id,
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return milestoneToCamelCase(data);
  },

  async updateMilestone(id, milestone) {
    const { data, error } = await supabase
      ?.from('project_milestones')
      ?.update(milestoneToSnakeCase(milestone))
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;
    
    const updatedMilestone = milestoneToCamelCase(data);
    
    // Send milestone completed email if status is completed
    if (milestone?.status === 'completed') {
      try {
        const milestoneDetails = await supabase
          ?.from('project_milestones')
          ?.select('*, projects(project_number, progress_percentage, client_id, leads(name, email))')
          ?.eq('id', id)
          ?.single();
        
        const clientEmail = milestoneDetails?.data?.projects?.leads?.email;
        const clientName = milestoneDetails?.data?.projects?.leads?.name;
        const projectData = {
          projectNumber: milestoneDetails?.data?.projects?.project_number,
          progressPercentage: milestoneDetails?.data?.projects?.progress_percentage,
        };
        
        if (clientEmail) {
          await emailService?.sendMilestoneCompletedEmail(
            projectData,
            updatedMilestone,
            clientEmail,
            clientName
          );
        }
      } catch (emailError) {
        console.error('Failed to send milestone completed email:', emailError);
      }
    }
    
    return updatedMilestone;
  },

  async deleteMilestone(id) {
    const { error } = await supabase?.from('project_milestones')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  subscribeToChanges(callback) {
    const channel = supabase
      ?.channel('projects')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'projects' }, (payload) => {
        callback(payload);
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  subscribeToMilestoneChanges(callback) {
    const channel = supabase
      ?.channel('project_milestones')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'project_milestones' }, (payload) => {
        callback(payload);
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  getActivities,
  createActivity,
};